import React from 'react';
import styled, { keyframes } from 'styled-components';

const COMMENTS = 4;

const CommentBoxSkeleton = () => (
  <Wrapper>
    {/* Title */}
    <TitleBar />
    <SubBar $wide />

    {/* Auth buttons */}
    <AuthRow>
      <SkBtn />
      <SkBtn />
      <SkBtn />
    </AuthRow>

    <Divider />

    {/* Fake comment items */}
    {Array.from({ length: COMMENTS }, (_, i) => (
      <CommentItem key={i} $delay={i * 120}>
        <Avatar />
        <Body>
          <Line width="38%" height="11px" $mb="8px" />
          <Line width="90%" />
          <Line width={i % 2 === 0 ? '75%' : '60%'} />
          <ReactRow>
            {[0, 1, 2, 3].map(j => (
              <Dot key={j} $delay={j * 60} />
            ))}
          </ReactRow>
        </Body>
      </CommentItem>
    ))}

    {/* Load-more ghost */}
    <LoadMore />
  </Wrapper>
);

export default CommentBoxSkeleton;

// ── animations ────────────────────────────────────────────────────────────────

const pulse = keyframes`
  0%, 100% {
    opacity: 0.35;
    background-position: -200% center;
  }
  50% {
    opacity: 0.55;
    background-position: 200% center;
  }
`;

const shimmer = keyframes`
  0%   { background-position: -400% center; }
  100% { background-position: 400% center; }
`;

// ── base skeleton piece ───────────────────────────────────────────────────────

const Bone = styled.div`
  background: linear-gradient(
    90deg,
    rgba(76, 0, 101, 0.4) 0%,
    rgba(148, 0, 211, 0.55) 40%,
    rgba(76, 0, 101, 0.4) 80%
  );
  background-size: 400% 100%;
  border-radius: 6px;
  animation: ${shimmer} 2.2s ease-in-out infinite;
  animation-delay: ${({ $delay }) => ($delay || 0)}ms;
`;

// ── layout ────────────────────────────────────────────────────────────────────

const Wrapper = styled.div`
  width: 100%;
  max-width: 780px;
  margin: 0 auto;
  padding: 28px 20px 24px;
  background: rgba(0, 0, 0, 0.6);
  border: 1px solid rgba(76, 0, 101, 0.4);
  border-radius: 16px;
  @media (max-width: 430px) { padding: 18px 12px; }
`;

const TitleBar = styled(Bone)`
  height: 22px;
  width: 62%;
  margin: 0 auto 10px;
`;

const SubBar = styled(Bone)`
  height: 12px;
  width: ${({ $wide }) => $wide ? '45%' : '30%'};
  margin: 0 auto 22px;
  opacity: 0.6;
`;

const AuthRow = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 22px;
`;

const SkBtn = styled(Bone)`
  flex: 1;
  height: 38px;
  border-radius: 8px;
`;

const Divider = styled.div`
  height: 1px;
  background: rgba(148, 0, 211, 0.15);
  margin-bottom: 20px;
`;

const CommentItem = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
  animation: ${pulse} 1.8s ease-in-out infinite;
  animation-delay: ${({ $delay }) => ($delay || 0)}ms;
`;

const Avatar = styled(Bone)`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  flex-shrink: 0;
`;

const Body = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Line = styled(Bone)`
  height: ${({ height }) => height || '13px'};
  width: ${({ width }) => width || '100%'};
  margin-bottom: ${({ $mb }) => $mb || '0'};
`;

const ReactRow = styled.div`
  display: flex;
  gap: 6px;
  margin-top: 4px;
`;

const Dot = styled(Bone)`
  width: 26px;
  height: 22px;
  border-radius: 6px;
`;

const LoadMore = styled(Bone)`
  display: block;
  width: 160px;
  height: 34px;
  border-radius: 20px;
  margin: 4px auto 0;
  opacity: 0.4;
`;
